import { useCallback, useId, useState } from "react";
import { InstanceSettings, type InstanceSettingsResponse } from "@spf/contracts";
import { api } from "../../lib/client.js";
import { useAsync } from "../../lib/useAsync.js";
import { formatDateTime } from "../../lib/format.js";
import { Failed, Loading, buttonClass } from "../ViewState.js";

/** The form holds strings, because a half-typed number is not a number. */
type Draft = Record<FieldName, string>;

type FieldName = keyof InstanceSettings;

interface FieldSpec {
  name: FieldName;
  label: string;
  hint: string;
  inputMode?: "numeric" | "decimal";
}

/**
 * Every instance setting, in the order an operator meets them: what the site
 * is, then how it judges freshness, then the two numbers the battery view
 * cannot work without.
 */
const FIELDS: readonly FieldSpec[] = [
  { name: "siteName", label: "Site name", hint: "Shown in the header and the browser tab." },
  { name: "timezone", label: "Timezone", hint: "An IANA name, such as Africa/Lagos." },
  { name: "currency", label: "Currency", hint: "A three-letter code, such as NGN." },
  {
    name: "dataStaleSeconds",
    label: "Stale after (seconds)",
    hint: "How old the newest reading may be before every view marks itself stale. This hardware reports every five minutes.",
    inputMode: "numeric",
  },
  {
    name: "batteryCapacityKwh",
    label: "Battery capacity (kWh)",
    hint: "The inverter reports a percentage and nothing that percentage is of, so runtime cannot be worked out without this.",
    inputMode: "decimal",
  },
  {
    name: "batteryReserveSocPct",
    label: "Reserve floor (%)",
    hint: "How far down the bank may be taken before it counts as empty. Runtime is measured to here, not to zero.",
    inputMode: "decimal",
  },
];

/**
 * The settings editor: the first screen in this application that writes.
 *
 * Held apart from the page that shows it, because the header's settings dialog
 * shows the same editor. One form, one write path, two places it appears.
 *
 * Two permissions, not one. `settings:read` puts this page in the navigation,
 * and a viewer holds it -- seeing how the instance is configured is not the
 * same as changing it. `settings:write` is what turns the fields into a form.
 * Hiding the form is presentation only; the endpoint enforces the same
 * permission, so a reader without it cannot write by other means either.
 *
 * The saved response replaces the draft rather than the draft being kept. The
 * endpoint returns the stored snapshot instead of echoing the body, and the
 * two differ wherever a coercion applied -- typing `900.4` into a whole-second
 * field and being shown `900.4` afterwards would be the screen lying about
 * what the database holds.
 */
export function SettingsEditor({ canWrite }: { canWrite: boolean }) {
  const { state, reload } = useAsync(
    api.settings,
    "Could not load settings. The server may be unreachable.",
  );
  // What the last save stored. Held here rather than re-fetching, because the
  // endpoint already answers with the stored snapshot -- and because a reload
  // passes back through the loading state, which unmounts the form and takes
  // the confirmation with it before anyone has read it.
  const [stored, setStored] = useState<InstanceSettingsResponse | null>(null);
  const [saved, setSaved] = useState(false);

  const onSaved = useCallback((next: InstanceSettingsResponse) => {
    setStored(next);
    setSaved(true);
  }, []);

  const current = stored ?? (state.kind === "ready" ? state.value : null);

  return (
    <>
      {state.kind === "loading" && stored === null && <Loading label="Loading settings…" />}
      {state.kind === "error" && stored === null && (
        <Failed message={state.message} onRetry={reload} />
      )}
      {current !== null && (
        <>
          <Editor
            // Remounts when the stored values change, so the draft is rebuilt
            // from what was actually saved rather than merged into stale state.
            key={current.updatedAt}
            values={current.values}
            updatedAt={current.updatedAt}
            canWrite={canWrite}
            onSaved={onSaved}
            onEdited={() => {
              setSaved(false);
            }}
          />
          {/* Outside the keyed form on purpose: a save changes `updatedAt`,
              which remounts the form, and a confirmation living inside it
              would be destroyed by the very success it reports. */}
          <p role="status" className="mt-4 text-sm text-ink-muted">
            {saved ? "Settings saved." : ""}
          </p>
        </>
      )}
    </>
  );
}

function Editor({
  values,
  updatedAt,
  canWrite,
  onSaved,
  onEdited,
}: {
  values: InstanceSettings;
  updatedAt: string;
  canWrite: boolean;
  onSaved: (next: InstanceSettingsResponse) => void;
  onEdited: () => void;
}) {
  const [draft, setDraft] = useState<Draft>(() => toDraft(values));
  const [problem, setProblem] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const set = useCallback(
    (name: FieldName, value: string) => {
      setDraft((current) => ({ ...current, [name]: value }));
      onEdited();
    },
    [onEdited],
  );

  async function submit(event: React.FormEvent): Promise<void> {
    event.preventDefault();
    setProblem(null);

    // Parsed against the shared contract, which is the same schema the server
    // validates with. Catching it here saves a round trip and names the field;
    // the server still decides, because a client is not a gate.
    const parsed = InstanceSettings.safeParse(draft);
    if (!parsed.success) {
      setProblem(firstProblem(parsed.error));
      return;
    }

    setSaving(true);
    try {
      onSaved(await api.saveSettings(parsed.data));
    } catch (error) {
      setProblem(error instanceof Error ? error.message : "Could not save settings.");
    } finally {
      setSaving(false);
    }
  }

  if (!canWrite) return <ReadOnly values={values} updatedAt={updatedAt} />;

  return (
    <form
      // `void`, because a form handler is expected to return nothing and an
      // async one returns a promise no caller awaits. Rejections are already
      // handled inside `submit`.
      onSubmit={(event) => {
        void submit(event);
      }}
      className="flex flex-col gap-5"
    >
      <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
        {FIELDS.map((field) => (
          <Field
            key={field.name}
            spec={field}
            value={draft[field.name]}
            onChange={(next) => {
              set(field.name, next);
            }}
          />
        ))}
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <button type="submit" className={buttonClass} disabled={saving}>
          {saving ? "Saving…" : "Save settings"}
        </button>
        <p className="text-xs text-ink-muted">
          Last changed <time dateTime={updatedAt}>{formatDateTime(updatedAt)}</time>.
        </p>
      </div>

      {/* Both live regions are always mounted. A `role="alert"` that appears
          with its text already in it is announced unreliably; an empty one that
          is filled is announced every time. */}
      <p role="alert" className="text-sm text-danger-ink">
        {problem ?? ""}
      </p>
    </form>
  );
}

function Field({
  spec,
  value,
  onChange,
}: {
  spec: FieldSpec;
  value: string;
  onChange: (value: string) => void;
}) {
  const id = useId();
  const hintId = `${id}-hint`;

  return (
    <div className="flex flex-col gap-1.5">
      <label className="text-sm font-medium text-ink" htmlFor={id}>
        {spec.label}
      </label>
      <input
        id={id}
        name={spec.name}
        type="text"
        inputMode={spec.inputMode}
        value={value}
        aria-describedby={hintId}
        onChange={(event) => {
          onChange(event.target.value);
        }}
        className="rounded-2xl border border-line bg-page px-4 py-2.5 text-sm text-ink transition-colors focus:border-accent"
      />
      <span id={hintId} className="text-xs text-ink-muted">
        {spec.hint}
      </span>
    </div>
  );
}

function ReadOnly({ values, updatedAt }: { values: InstanceSettings; updatedAt: string }) {
  return (
    <div className="flex flex-col gap-4">
      <p className="text-sm text-ink-muted">
        These are the instance settings. Changing them needs an administrator.
      </p>
      <dl className="grid grid-cols-1 gap-x-6 gap-y-3 sm:grid-cols-2">
        {FIELDS.map((field) => (
          <div
            key={field.name}
            className="flex items-baseline justify-between gap-4 border-b border-line pb-2"
          >
            <dt className="text-sm text-ink-muted">{field.label}</dt>
            <dd className="tabular text-sm font-medium text-ink">{String(values[field.name])}</dd>
          </div>
        ))}
      </dl>
      <p className="text-xs text-ink-muted">
        Last changed <time dateTime={updatedAt}>{formatDateTime(updatedAt)}</time>.
      </p>
    </div>
  );
}

function toDraft(values: InstanceSettings): Draft {
  const draft = {} as Draft;
  for (const field of FIELDS) draft[field.name] = String(values[field.name]);
  return draft;
}

/**
 * The first failure, named by its field. A list of six messages for one typo
 * is worse than one sentence pointing at the thing to fix.
 */
function firstProblem(error: {
  issues: readonly { path: PropertyKey[]; message: string }[];
}): string {
  const issue = error.issues[0];
  if (issue === undefined) return "Those settings are not valid.";
  const spec = FIELDS.find((field) => field.name === issue.path[0]);
  return spec === undefined ? issue.message : `${spec.label}: ${issue.message}`;
}
